import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Plus, FileText, Building, Users, Target, BookOpen, Star, Download, Loader2 } from 'lucide-react';
import { useToast } from '../components/Toast';

interface Template {
  id: string;
  title: string;
  category: string;
  description: string;
  organization: string;
  pages: number;
  rating: number;
  downloads: number;
  tags: string[];
  preview: string;
  isPremium: boolean;
}

const Templates: React.FC = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedOrganization, setSelectedOrganization] = useState('all');

  const categories = [
    { value: 'all', label: '전체', icon: FileText },
    { value: 'policy', label: '정책보고서', icon: Building },
    { value: 'business', label: '사업계획서', icon: Target },
    { value: 'research', label: '연구보고서', icon: BookOpen },
    { value: 'grant', label: '공모사업', icon: Users },
    { value: 'analysis', label: '분석보고서', icon: FileText }
  ];

  const organizations = [
    { value: 'all', label: '전체' },
    { value: 'government', label: '정부기관' },
    { value: 'research', label: '연구기관' },
    { value: 'enterprise', label: '기업' },
    { value: 'university', label: '대학' },
    { value: 'consulting', label: '컨설팅' }
  ];

  const [templates, setTemplates] = useState<Template[]>([]);
  const [templatesLoading, setTemplatesLoading] = useState(true);

  useEffect(() => {
    const fetchTemplates = async () => {
      setTemplatesLoading(true);
      try {
        const token = localStorage.getItem('proposalcraft_access_token');
        const hostname = window.location.hostname;
        const protocol = window.location.protocol;
        const baseUrl = (hostname !== 'localhost' && hostname !== '127.0.0.1')
          ? `${protocol}//${hostname}:8001` : 'http://localhost:8001';

        const response = await fetch(`${baseUrl}/api/document-templates/templates`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });

        if (response.ok) {
          const data = await response.json();
          const items = Array.isArray(data) ? data : (data.templates || data.items || []);
          if (items.length > 0) {
            setTemplates(items.map((t: any, i: number) => ({
              id: t.id || String(i + 1),
              title: t.title || t.name || '',
              category: t.category || 'grant',
              description: t.description || '',
              organization: t.organization || '',
              pages: t.pages || 0,
              rating: t.rating || 0,
              downloads: t.downloads || 0,
              tags: t.tags || [],
              preview: t.preview || t.description || '',
              isPremium: t.is_premium || false,
            })));
            setTemplatesLoading(false);
            return;
          }
        }
      } catch (err) {
        console.error('Failed to fetch templates:', err);
      }
      // 백엔드에 템플릿이 없거나 실패 시 기본 템플릿 제공
      setTemplates([
        { id: '1', title: '공모사업 신청서 템플릿', category: 'grant', description: '정부 지원 사업 공모에 최적화된 신청서 템플릿', organization: '정부기관', pages: 60, rating: 4.9, downloads: 0, tags: ['공모사업', '정부지원'], preview: '공모사업 제안서 표준 형식', isPremium: false },
        { id: '2', title: '사업계획서 전문 템플릿', category: 'business', description: '체계적인 사업계획서 작성 템플릿', organization: '컨설팅', pages: 80, rating: 4.6, downloads: 0, tags: ['사업계획', '스타트업'], preview: '사업계획서 표준 구조', isPremium: false },
        { id: '3', title: '연구보고서 학술 템플릿', category: 'research', description: '학술 연구 결과를 정리하는 템플릿', organization: '대학', pages: 150, rating: 4.7, downloads: 0, tags: ['학술연구', '논문'], preview: '연구보고서 표준 형식', isPremium: false },
        { id: '4', title: '정책보고서 표준 템플릿', category: 'policy', description: '정책연구 보고서 표준 템플릿', organization: '연구기관', pages: 120, rating: 4.8, downloads: 0, tags: ['정책연구', '표준'], preview: '정책보고서 형식', isPremium: false },
      ]);
      setTemplatesLoading(false);
    };

    fetchTemplates();
  }, []);

  const filteredTemplates = templates.filter(template => {
    const matchesSearch = template.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         template.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         template.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()));
    
    const matchesCategory = selectedCategory === 'all' || template.category === selectedCategory;
    const matchesOrganization = selectedOrganization === 'all' || template.organization === selectedOrganization;
    
    return matchesSearch && matchesCategory && matchesOrganization;
  });

  const handleTemplateSelect = (template: Template) => {
    navigate('/projects', {
      state: {
        selectedTemplate: template,
        templateContent: template.description,
      },
    });
  };

  const handleTemplateDownload = async (template: Template) => {
    try {
      const token = localStorage.getItem('proposalcraft_access_token');
      const baseUrl =
        window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
          ? 'http://localhost:8001'
          : `${window.location.protocol}//${window.location.hostname}:8001`;

      const response = await fetch(
        `${baseUrl}/api/document-templates/generate-template/${encodeURIComponent(template.category)}`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        const blob = new Blob([data.content || JSON.stringify(data, null, 2)], {
          type: 'text/plain;charset=utf-8',
        });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${template.title}.md`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      }
    } catch (err) {
      console.error('Template download failed:', err);
      toast.error('템플릿 다운로드에 실패했습니다.');
    }
  };

  return (
    <div className="space-y-6">
      {/* 헤더 */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">템플릿 라이브러리</h1>
          <p className="text-gray-600 mt-2">AI가 학습한 다양한 보고서 템플릿을 활용하세요</p>
        </div>
        <button className="btn-primary">
          <Plus className="h-5 w-5 mr-2" />
          새 템플릿 생성
        </button>
      </div>

      {/* 검색 및 필터 */}
      <div className="card">
        <div className="flex items-center space-x-4 mb-6">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
            <input
              type="text"
              placeholder="템플릿 검색..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent w-full"
            />
          </div>
          
          <select
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          >
            {categories.map((category) => (
              <option key={category.value} value={category.value}>
                {category.label}
              </option>
            ))}
          </select>
          
          <select
            value={selectedOrganization}
            onChange={(e) => setSelectedOrganization(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          >
            {organizations.map((org) => (
              <option key={org.value} value={org.value}>
                {org.label}
              </option>
            ))}
          </select>
        </div>

        <div className="flex items-center space-x-4 text-sm text-gray-600">
          <span>총 {filteredTemplates.length}개의 템플릿</span>
          <span>•</span>
          <span>무료: {filteredTemplates.filter(t => !t.isPremium).length}개</span>
          <span>•</span>
          <span>프리미엄: {filteredTemplates.filter(t => t.isPremium).length}개</span>
        </div>
      </div>

      {/* 템플릿 그리드 */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredTemplates.map((template) => (
          <div key={template.id} className="card hover:shadow-medium transition-shadow duration-200 cursor-pointer group">
            {/* 템플릿 헤더 */}
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center space-x-2">
                {template.isPremium && (
                  <span className="px-2 py-1 bg-yellow-100 text-yellow-800 text-xs font-medium rounded-full">
                    프리미엄
                  </span>
                )}
                <span className="px-2 py-1 bg-primary-100 text-primary-800 text-xs font-medium rounded-full">
                  {template.category === 'policy' ? '정책' :
                   template.category === 'business' ? '사업' :
                   template.category === 'research' ? '연구' :
                   template.category === 'grant' ? '공모' :
                   template.category === 'analysis' ? '분석' : '기타'}
                </span>
              </div>
              <div className="flex items-center space-x-1 text-yellow-500">
                <Star className="h-4 w-4 fill-current" />
                <span className="text-sm font-medium">{template.rating}</span>
              </div>
            </div>

            {/* 템플릿 제목 및 설명 */}
            <h3 className="text-lg font-semibold text-gray-900 mb-2 group-hover:text-primary-600 transition-colors">
              {template.title}
            </h3>
            <p className="text-gray-600 text-sm mb-4 line-clamp-3">
              {template.description}
            </p>

            {/* 템플릿 메타데이터 */}
            <div className="space-y-2 mb-4">
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-500">조직</span>
                <span className="text-gray-900 font-medium">{template.organization}</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-500">페이지</span>
                <span className="text-gray-900 font-medium">{template.pages}페이지</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-500">다운로드</span>
                <span className="text-gray-900 font-medium">{template.downloads.toLocaleString()}회</span>
              </div>
            </div>

            {/* 태그 */}
            <div className="flex flex-wrap gap-2 mb-4">
              {template.tags.slice(0, 3).map((tag) => (
                <span key={tag} className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded-full">
                  {tag}
                </span>
              ))}
              {template.tags.length > 3 && (
                <span className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded-full">
                  +{template.tags.length - 3}
                </span>
              )}
            </div>

            {/* 액션 버튼 */}
            <div className="flex space-x-2">
              <button
                onClick={() => handleTemplateSelect(template)}
                className="btn-primary flex-1 text-sm py-2"
              >
                <FileText className="h-4 w-4 mr-2" />
                사용하기
              </button>
              <button
                onClick={() => handleTemplateDownload(template)}
                className="btn-secondary text-sm py-2 px-3"
              >
                <Download className="h-4 w-4" />
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* 빈 상태 */}
      {filteredTemplates.length === 0 && (
        <div className="text-center py-12">
          <FileText className="mx-auto h-12 w-12 text-gray-400 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">템플릿을 찾을 수 없습니다</h3>
          <p className="text-gray-600 mb-6">
            검색 조건을 변경하거나 다른 카테고리를 선택해보세요.
          </p>
          <button
            onClick={() => {
              setSearchTerm('');
              setSelectedCategory('all');
              setSelectedOrganization('all');
            }}
            className="btn-primary"
          >
            필터 초기화
          </button>
        </div>
      )}
    </div>
  );
};

export default Templates; 